import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  FileBarChart, RefreshCw, Check, Clock, AlertCircle, ExternalLink,
  Search, ChevronRight, Shield,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending_coder_review: 'bg-amber-100 text-amber-700',
  coder_reviewed: 'bg-blue-100 text-blue-700',
  ready_for_claim: 'bg-emerald-100 text-emerald-700',
  held: 'bg-red-100 text-red-700',
  draft: 'bg-slate-100 text-slate-600',
};

export default function CodingQueue() {
  const navigate = useNavigate();
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reviewing, setReviewing] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/coding-queue');
      setCharges(res.data.charges || []);
    } catch { toast.error('Failed to load queue'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleReview = async (chargeId, action, notes = '') => {
    setReviewing(chargeId);
    try {
      await api.post(`/charges/${chargeId}/review`, { action, notes });
      toast.success(action === 'approve' ? 'Charge approved — ready for claim' : `Charge ${action}`);
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setReviewing(null); }
  };

  const handleCreateClaim = async (chargeId) => {
    try {
      const res = await api.post(`/charges/${chargeId}/create-claim`);
      toast.success(`Claim created: ${res.data.claim.id.slice(0, 8)}...`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const filtered = search
    ? charges.filter(c => (c._patient_name || '').toLowerCase().includes(search.toLowerCase()) || (c.primary_code || '').includes(search) || (c.provider_name || '').toLowerCase().includes(search.toLowerCase()))
    : charges;

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4" data-testid="coding-queue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <FileBarChart className="w-5 h-5 text-primary" /> Coding Queue
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">{charges.length} charges pending review</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchQueue}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by patient, code, or provider..." className="pl-8 h-9 text-sm" data-testid="coding-search" />
      </div>

      {/* Queue */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center">
          <Shield className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{search ? 'No charges match your search' : 'No charges pending review'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {/* Header */}
          <div className="grid grid-cols-[1fr_100px_100px_120px_80px_140px] gap-2 px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
            <div>Patient</div>
            <div>DOS</div>
            <div>Provider</div>
            <div>Codes</div>
            <div>Status</div>
            <div>Actions</div>
          </div>

          {filtered.map(charge => (
            <Card key={charge.id} className="hover:shadow-sm transition-shadow" data-testid={`charge-row-${charge.id}`}>
              <CardContent className="p-3">
                <div className="grid grid-cols-[1fr_100px_100px_120px_80px_140px] gap-2 items-center">
                  <div>
                    <p className="text-sm font-medium">{charge._patient_name || charge.patient_id?.slice(0, 8)}</p>
                    <p className="text-[10px] text-muted-foreground">{charge.visit_type}</p>
                  </div>
                  <div className="text-xs tabular-nums">{charge.date_of_service}</div>
                  <div className="text-xs truncate">{charge.provider_name?.split(' ').slice(-1)[0]}</div>
                  <div className="flex flex-wrap gap-1">
                    {charge.primary_code && <Badge variant="outline" className="text-[9px] font-mono h-5">{charge.primary_code}</Badge>}
                    {(charge.diagnosis_codes || []).slice(0, 2).map((dx, i) => (
                      <Badge key={i} variant="outline" className="text-[9px] font-mono h-5 bg-amber-50 text-amber-700">{dx}</Badge>
                    ))}
                    {(charge.diagnosis_codes || []).length > 2 && <span className="text-[9px] text-muted-foreground">+{charge.diagnosis_codes.length - 2}</span>}
                  </div>
                  <div>
                    <Badge className={`text-[8px] ${STATUS_COLORS[charge.charge_status] || 'bg-slate-100'}`}>
                      {charge.charge_status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" className="h-7 text-[10px] px-2 gap-1 bg-emerald-600 hover:bg-emerald-700"
                      disabled={reviewing === charge.id}
                      onClick={() => handleReview(charge.id, 'approve')}
                      data-testid={`approve-charge-${charge.id}`}>
                      <Check className="w-3 h-3" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[10px] px-2"
                      onClick={() => handleReview(charge.id, 'hold', 'Needs review')}
                      data-testid={`hold-charge-${charge.id}`}>
                      Hold
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => navigate(`/visit/${charge.encounter_id}`)}
                      data-testid={`open-encounter-${charge.id}`}>
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
